import AddEditFormCst from "@components/AddEditFormCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { ColumnInterface } from "@components/Table/types";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { Box, Container, Typography } from "@mui/material";
import { ServicesURL, getAllErrorsAsString, uploadFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "src/services/config";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import useFetchCertificatePlaceholders from "@hooks/useFetchCertificatePlaceholders";
import {
  useValidatePlaceholders,
  useValidatePlaceholdersCustom,
} from "@hooks/useValidatePlaceholders";
import { useHtmlToZip } from "@hooks/useHtmlToZip";
import { useAddBackground, useEditHTML } from "@hooks/useEditHTML";

const EditCertificates: React.FC = () => {
  const { t } = useTranslation(["certificates-page"]);
  const { idCertificate, id } = useParams();
  const navigate = useNavigate();

  const { columns, actionsForForms, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getCertificates}`,
    idCertificate,
    PagesID["certificates"],
  );

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerEditCertificate } = useSWRMutation(
    `${ServicesURL.getCertificates}/${idCertificate}`,
    (url: string, arg: { arg: any }) => {
      return () =>
        axiosInstance.patch(`${url}`, arg.arg, {
          headers: { "Content-Type": "application/json-patch+json" },
        });
    },
  );

  const { data: dataPlaceholders } = useFetchCertificatePlaceholders();

  const handleFileUnzip = useValidatePlaceholders();

  const handleFileUnzipCustom = useValidatePlaceholdersCustom();
  const handleHtmlToZip = useHtmlToZip();
  const handleEditHTML = useEditHTML();
  const handleAddBackground = useAddBackground();
  // fetcher<any>(`${url}`, MethodType.PATCH, arg.arg)

  const getInitialOutcome = () => {
    return {
      certificate: {
        code: undefined,
        message: t("edit.outcome.certificate.messageLoading"),
        title: t("edit.outcome.certificate.title"),
      },
      uploadTemplate: {
        code: undefined,
        message: t("edit.outcome.upload-template.messageLoading"),
        title: t("edit.outcome.upload-template.title"),
      },
      uploadIcon: {
        code: undefined,
        message: t("edit.outcome.upload-icon.messageLoading"),
        title: t("edit.outcome.upload-icon.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    certificate: {
      code: undefined,
      message: t("edit.outcome.certificate.messageLoading"),
      title: t("edit.outcome.certificate.title"),
    },
    uploadTemplate: {
      code: undefined,
      message: t("edit.outcome.upload-template.messageLoading"),
      title: t("edit.outcome.upload-template.title"),
    },
    uploadIcon: {
      code: undefined,
      message: t("edit.outcome.upload-icon.messageLoading"),
      title: t("edit.outcome.upload-icon.title"),
    },
  };

  const [outcome, setOutcome] =
    React.useState<OutcomesInterface>(initialOutcome);
  const [openOutcome, setOpenOutcome] = React.useState(false);

  const onSubmit = async (
    inputForm: any,
    fileNotificationsAsset: any,
    fileTemplateAsset: any,
    participations: any,
    fileTheme: any,
    imagePath: any,
    pdfTemplatePath: any,
    image: any,
    document: any,
    fileUploaded: any,
    isCustomizable: boolean | undefined,
    backgroundHTML: string | undefined,
    orientationHTML: string | undefined,
  ) => {
    setOpenOutcome(true);

    const initialOutcomeState: any = getInitialOutcome();

    let rawTemplate = inputForm.templateBody || "<div><br></div>";
    let templateForPrint: string | null = null;
    if (rawTemplate !== "<div><br></div>") {
      templateForPrint = await handleEditHTML(rawTemplate);
      templateForPrint = await handleAddBackground(
        templateForPrint,
        backgroundHTML,
        orientationHTML ?? "portrait",
      );
    } else {
      rawTemplate = null;
    }

    if (!isCustomizable) {
      rawTemplate = null as any;
      templateForPrint = null;
    }

    if (!pdfTemplatePath && !templateForPrint) {
      delete initialOutcomeState.uploadTemplate;
    }
    if (!image) {
      delete initialOutcomeState.uploadIcon;
    }
    setOutcome(initialOutcomeState);

    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));

    try {
      const formattedParams: any = [
        {
          op: "replace",
          path: "/description",
          value: inputForm.description,
        },
        {
          op: "replace",
          path: "/templateBody",
          value: rawTemplate,
        },
      ];

      await triggerEditCertificate(formattedParams);
      newOutcome.certificate.code = "OK";
      newOutcome.certificate.message = t("edit.outcome.certificate.messageOK");

      if (pdfTemplatePath) {
        try {
          const { placeholders } = await handleFileUnzip(
            pdfTemplatePath.file.get("file"),
            dataPlaceholders,
          );

          let formDatapdf = new FormData();
          formDatapdf.append("templateZip", pdfTemplatePath.file.get("file"));
          const placeholdersQueryParams = placeholders
            ? `?placeholderIds=${placeholders.join(",")}`
            : "";
          await uploadFile(
            `${ServicesURL.getCertificates}/${idCertificate}/template${placeholdersQueryParams}`,
            formDatapdf,
            MethodType.PUT,
          );
          newOutcome.uploadTemplate.code = "OK";
          newOutcome.uploadTemplate.message = t(
            "edit.outcome.upload-template.messageOK",
          );
        } catch (error: any) {
          newOutcome.uploadTemplate.code = "KO";
          if (error.message) {
            newOutcome.uploadTemplate.message = getAllErrorsAsString({
              response: { data: { errors: [[{ message: error.message }]] } },
            }); // adatto il formato a ciò che si aspetta getAllErrorsAsString per retrocompatibilità
          } else {
            newOutcome.uploadTemplate.message = getAllErrorsAsString(error);
          }
        }
      }

      if (templateForPrint != null && !pdfTemplatePath) {
        try {
          const { zipFile } = await handleHtmlToZip(templateForPrint);
          const { placeholders } = await handleFileUnzipCustom(
            zipFile,
            dataPlaceholders,
          );

          let formDatapdf = new FormData();
          formDatapdf.append("templateZip", zipFile);
          const placeholdersQueryParams = placeholders
            ? `?placeholderIds=${placeholders.join(",")}`
            : "";
          await uploadFile(
            `${ServicesURL.getCertificates}/${idCertificate}/template${placeholdersQueryParams}`,
            formDatapdf,
            MethodType.PUT,
          );
          newOutcome.uploadTemplate.code = "OK";
          newOutcome.uploadTemplate.message = t(
            "edit.outcome.upload-template.messageOK",
          );
        } catch (error: any) {
          newOutcome.uploadTemplate.code = "KO";
          if (error.message) {
            newOutcome.uploadTemplate.message = getAllErrorsAsString({
              response: { data: { errors: [[{ message: error.message }]] } },
            }); // adatto il formato a ciò che si aspetta getAllErrorsAsString per retrocompatibilità
          } else {
            newOutcome.uploadTemplate.message = getAllErrorsAsString(error);
          }
        }
      }

      if (image) {
        try {
          let formDataIcon = new FormData();
          formDataIcon.append("image", image.file);
          await uploadFile(
            `${ServicesURL.getCertificates}/${idCertificate}/image`,
            formDataIcon,
            MethodType.PUT,
          );
          newOutcome.uploadIcon.code = "OK";
          newOutcome.uploadIcon.message = t(
            "edit.outcome.upload-icon.messageOK",
          );
        } catch (error) {
          newOutcome.uploadIcon.code = "KO";
          newOutcome.uploadIcon.message = getAllErrorsAsString(error);
        }
      }

      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.certificate.code = "KO";
      newOutcome.certificate.message = getAllErrorsAsString(error);
      if (pdfTemplatePath) {
        newOutcome.uploadTemplate.code = "KO";
        newOutcome.uploadTemplate.message = t(
          "edit.outcome.upload-template.messageErrorDuringCertificate",
        );
      }
      if (image) {
        newOutcome.uploadIcon.code = "KO";
        newOutcome.uploadIcon.message = t(
          "edit.outcome.upload-icon.messageErrorDuringCertificate",
        );
      }
      setOutcome(newOutcome);
    }
    mutate(inputForm);
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (el.accessorKey === "name") {
        return { ...el, disabled: true };
      } else {
        return { ...el };
      }
    });
  }, []);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-certificates.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("edit.hero-title")}
          description={t("edit.hero-description")}
        />
        {getColumns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Typography variant="h4" mb={2}>
              N.B.
              <br />I template dei certificati già rilasciati possono essere
              modificati senza l’aggiunta di nuovi placeholder.
            </Typography>
            <AddEditFormCst
              row={dataDetail}
              columns={getColumns}
              onSubmit={onSubmit}
              actionsForForms={
                actionsForForms &&
                actionsForForms.map(
                  (el: ColumnInterface, obj: ColumnInterface) => {
                    if (dataDetail && dataDetail.editable) {
                      if (el.accessorKey === "pdfTemplatePath") {
                        el.disabled = !dataPlaceholders; // disabilitato finché non termina la get dei placeholders
                        el.uploadCallback = (formData: FormData) => {
                          return formData;
                        };
                      }
                      return el;
                    } else {
                      return { ...el, disabled: true };
                    }
                  },
                )
              }
              backButton={() => {
                navigate(-1);
              }}
              fullWidth={true}
            />
          </Box>
        )}
      </Container>
      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            setOpenOutcome(!openOutcome);
            navigate(
              id
                ? `/corporate/${id}/detail/badge-certificates/certificates`
                : `/badge-certificates/certificates`,
            );
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditCertificates;
